import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, User, Plus, Search, Edit2, Trash2, ArrowLeft, Save, X, Phone, Mail, FileText, Package, Calendar, Settings } from 'lucide-react';
import Layout from './Layout';
import { useStore, getLocalISODate } from '../context/StoreContext';
import { useAlert } from '../context/AlertContext';
import './Dashboard.css'; // 기본 레이아웃 스타일 활용

const Vendors = () => {
    const navigate = useNavigate();
    const { user, vendors, updateVendors, isLoading } = useStore();
    const { showAlert } = useAlert();
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentVendor, setCurrentVendor] = useState({
        id: null,
        name: '',
        regDate: getLocalISODate(),
        manager: '',
        phone: '',
        email: '',
        bizNum: '',
        items: ''
    });

    // 보안 권한 체크 (useEffect 적용)
    React.useEffect(() => {
        if (!isLoading && (!user || user.role !== 'admin')) {
            navigate('/dashboard');
        }
    }, [user, isLoading, navigate]);

    if (isLoading || !user || user.role !== 'admin') return null;

    const filteredVendors = useMemo(() => {
        return vendors.filter(v =>
            (v.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (v.manager?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (v.items?.toLowerCase() || '').includes(searchTerm.toLowerCase())
        );
    }, [vendors, searchTerm]);

    const handleOpenAddModal = () => {
        setIsEditing(false);
        setCurrentVendor({
            id: Date.now(),
            name: '',
            regDate: getLocalISODate(),
            manager: '',
            phone: '',
            email: '',
            bizNum: '',
            items: ''
        });
        setShowModal(true);
    };

    const handleOpenEditModal = (vendor) => {
        setIsEditing(true);
        setCurrentVendor({ ...vendor });
        setShowModal(true);
    };

    const handleSave = () => {
        if (!currentVendor.name) return showAlert('거래처명을 입력해 주세요.');

        if (isEditing) {
            updateVendors(vendors.map(v => v.id === currentVendor.id ? currentVendor : v));
        } else {
            updateVendors([...vendors, currentVendor]);
        }
        setShowModal(false);
    };

    const handleDelete = (id) => {
        showAlert('정말 이 거래처를 삭제하시겠습니까?', {
            type: 'confirm',
            onConfirm: () => {
                showAlert('보안을 위해 상위 비밀번호(Master Key)를 입력해주세요.', {
                    type: 'prompt',
                    onConfirm: (masterKey) => {
                        if (masterKey === 'kkh1234') {
                            updateVendors(vendors.filter(v => v.id !== id));
                        } else {
                            showAlert('상위 비밀번호가 일치하지 않습니다. 권한이 없습니다.');
                        }
                    }
                });
            }
        });
    };

    return (
        <Layout>
            <div style={{ padding: '32px', maxWidth: '1600px', margin: '0 auto' }}>
                <div className="content-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
                        <button onClick={() => navigate('/dashboard')} className="icon-button" title="뒤로가기">
                            <ArrowLeft size={20} />
                        </button>
                        <h1>거래처 관리 (PC 버전)</h1>
                    </div>
                    <p style={{ color: '#94a3b8' }}>자재 납품 업체 및 협력사 정보를 통합적으로 관리하세요. (PC 환경에 최적화되어 있습니다.)</p>
                </div>

                <div className="glass-card" style={{ marginBottom: '24px', padding: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                        <div className="search-box" style={{ flex: 1, position: 'relative', maxWidth: '500px' }}>
                            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
                            <input
                                type="text"
                                placeholder="거래처명, 담당자, 품목으로 검색..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 12px 12px 40px',
                                    borderRadius: '8px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    color: '#fff',
                                    outline: 'none',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>
                        <button onClick={handleOpenAddModal} className="full-width-button" style={{ width: 'auto', padding: '12px 24px', fontSize: '1rem' }}>
                            <Plus size={20} style={{ marginRight: '8px' }} /> 거래처 신규 등록
                        </button>
                    </div>
                </div>

                <div className="recent-activities" style={{ marginTop: '0' }}>
                    <div className="activities-list is-admin">
                        {/* 헤더 부분 */}
                        <div className="activity-item header-item" style={{
                            gridTemplateColumns: '1.5fr 1.2fr 1fr 1.2fr 1.5fr 1fr 2fr 100px',
                            padding: '12px 20px',
                            background: 'rgba(255, 255, 255, 0.03)',
                            borderRadius: '12px 12px 0 0'
                        }}>
                            <div className="col-name">거래처명</div>
                            <div style={{ textAlign: 'left' }}>사업자번호</div>
                            <div style={{ textAlign: 'left' }}>담당자</div>
                            <div style={{ textAlign: 'left' }}>연락처</div>
                            <div style={{ textAlign: 'left' }}>이메일</div>
                            <div style={{ textAlign: 'left' }}>등록일</div>
                            <div style={{ textAlign: 'left' }}>주요 품목</div>
                            <div className="col-actions" style={{ textAlign: 'center' }}>설정</div>
                        </div>

                        {/* 리스트 부분 */}
                        {filteredVendors.length > 0 ? (
                            filteredVendors.map(vendor => (
                                <div key={vendor.id} className="activity-item row-item" style={{
                                    gridTemplateColumns: '1.5fr 1.2fr 1fr 1.2fr 1.5fr 1fr 2fr 100px',
                                    padding: '16px 20px',
                                    alignItems: 'center',
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                                }}>
                                    <div className="col-name">
                                        <h4 style={{ color: '#38bdf8', fontWeight: '700', fontSize: '1.05rem' }}>{vendor.name}</h4>
                                    </div>
                                    <div style={{ color: '#94a3b8', fontSize: '14px' }}>{vendor.bizNum || '-'}</div>
                                    <div style={{ fontWeight: '600', color: '#f8fafc' }}>{vendor.manager}</div>
                                    <div style={{ color: '#e2e8f0', fontSize: '14px' }}>{vendor.phone}</div>
                                    <div style={{ color: '#94a3b8', fontSize: '14px' }}>{vendor.email}</div>
                                    <div style={{ color: '#64748b', fontSize: '13px' }}>{vendor.regDate}</div>
                                    <div style={{ color: '#cbd5e1', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={vendor.items}>
                                        {vendor.items}
                                    </div>
                                    <div className="col-actions" style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                                        <button onClick={() => handleOpenEditModal(vendor)} className="icon-button" title="정보 수정">
                                            <Settings size={18} />
                                        </button>
                                        <button onClick={() => handleDelete(vendor.id)} className="icon-button" style={{ color: '#ef4444' }} title="거래처 삭제">
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-data-msg" style={{ padding: '80px', textAlign: 'center', color: '#64748b', background: 'rgba(255, 255, 255, 0.01)' }}>
                                등록된 거래처 정보가 없습니다.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 등록/수정 모달 */}
            {showModal && (
                <div className="modal-overlay">
                    <div className="glass-card" style={{ width: '100%', maxWidth: '500px', padding: '32px', position: 'relative' }}>
                        <button
                            onClick={() => setShowModal(false)}
                            style={{ position: 'absolute', right: '20px', top: '20px', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
                        >
                            <X size={24} />
                        </button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: 'rgba(56, 189, 248, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Users size={20} color="#38bdf8" />
                            </div>
                            <h2 style={{ margin: 0, fontSize: '1.5rem' }}>거래처 {isEditing ? '정보 수정' : '신규 등록'}</h2>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div className="input-field">
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#94a3b8', marginBottom: '8px' }}>
                                    <Users size={14} /> 거래처명
                                </label>
                                <input
                                    type="text"
                                    value={currentVendor.name}
                                    onChange={e => setCurrentVendor({ ...currentVendor, name: e.target.value })}
                                    placeholder="업체명을 입력하세요"
                                    style={{ width: '100%', padding: '12px', borderRadius: '8px', backgroundColor: '#1e293b', border: '1px solid #334155', color: '#fff' }}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div className="input-field">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#94a3b8', marginBottom: '8px' }}>
                                        <Calendar size={14} /> 등록 일자
                                    </label>
                                    <input
                                        type="date"
                                        value={currentVendor.regDate}
                                        onChange={e => setCurrentVendor({ ...currentVendor, regDate: e.target.value })}
                                        style={{ width: '100%', padding: '12px', borderRadius: '8px', backgroundColor: '#1e293b', border: '1px solid #334155', color: '#fff' }}
                                    />
                                </div>
                                <div className="input-field">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#94a3b8', marginBottom: '8px' }}>
                                        <FileText size={14} /> 사업자번호
                                    </label>
                                    <input
                                        type="text"
                                        value={currentVendor.bizNum}
                                        onChange={e => setCurrentVendor({ ...currentVendor, bizNum: e.target.value })}
                                        placeholder="000-00-00000"
                                        style={{ width: '100%', padding: '12px', borderRadius: '8px', backgroundColor: '#1e293b', border: '1px solid #334155', color: '#fff' }}
                                    />
                                </div>
                            </div>

                            <div className="input-field">
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#94a3b8', marginBottom: '8px' }}>
                                    <User size={14} /> 담당자 성함
                                </label>
                                <input
                                    type="text"
                                    value={currentVendor.manager}
                                    onChange={e => setCurrentVendor({ ...currentVendor, manager: e.target.value })}
                                    placeholder="담당자 성함을 입력하세요"
                                    style={{ width: '100%', padding: '12px', borderRadius: '8px', backgroundColor: '#1e293b', border: '1px solid #334155', color: '#fff' }}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div className="input-field">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#94a3b8', marginBottom: '8px' }}>
                                        <Phone size={14} /> 전화번호
                                    </label>
                                    <input
                                        type="text"
                                        value={currentVendor.phone}
                                        onChange={e => setCurrentVendor({ ...currentVendor, phone: e.target.value })}
                                        placeholder="010-0000-0000"
                                        style={{ width: '100%', padding: '12px', borderRadius: '8px', backgroundColor: '#1e293b', border: '1px solid #334155', color: '#fff' }}
                                    />
                                </div>
                                <div className="input-field">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#94a3b8', marginBottom: '8px' }}>
                                        <Mail size={14} /> 이메일 주소
                                    </label>
                                    <input
                                        type="email"
                                        value={currentVendor.email}
                                        onChange={e => setCurrentVendor({ ...currentVendor, email: e.target.value })}
                                        placeholder="example@mail.com"
                                        style={{ width: '100%', padding: '12px', borderRadius: '8px', backgroundColor: '#1e293b', border: '1px solid #334155', color: '#fff' }}
                                    />
                                </div>
                            </div>

                            <div className="input-field">
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#94a3b8', marginBottom: '8px' }}>
                                    <Package size={14} /> 주요 판매 품목
                                </label>
                                <textarea
                                    value={currentVendor.items}
                                    onChange={e => setCurrentVendor({ ...currentVendor, items: e.target.value })}
                                    placeholder="납품받는 주요 품목들을 입력하세요"
                                    style={{ width: '100%', padding: '12px', borderRadius: '8px', backgroundColor: '#1e293b', border: '1px solid #334155', color: '#fff', minHeight: '80px', resize: 'vertical' }}
                                />
                            </div>

                                <button
                                    onClick={handleSave}
                                    className="full-width-button"
                                    style={{ marginTop: '12px', height: '50px', fontSize: '1rem' }}
                                >
                                    <Save size={18} style={{ marginRight: '8px' }} /> 정보 저장하기
                                </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default Vendors;
